import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Mail, Lock, User, ArrowLeft, AlertCircle, CheckCircle, Phone, Eye, EyeOff } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { MASTER_EMAIL } from "../constants/api";
import { toast } from "sonner";
import { validatePhone as validatePhoneUtil, validateUserType as validateUserTypeUtil } from "../utils/validation";
import { sanitizePhoneInput } from "../utils/phoneUtils";
import GoogleSignInButton from "./GoogleSignInButton";

const AuthPage = () => {
  const [loginData, setLoginData] = useState({
    emailOrPhone: "",
    password: "",
    userType: "runner"
  });
  
  const [loginErrors, setLoginErrors] = useState({
    emailOrPhone: "",
    password: "",
    userType: "",
    general: ""
  });
  
  const [loginFieldTouched, setLoginFieldTouched] = useState({
    emailOrPhone: false,
    password: false,
    userType: false
  });
  
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    userType: "runner",
    city: "",
    state: "",
    runClubName: ""
  });
  
  const [registerErrors, setRegisterErrors] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    userType: "",
    city: "",
    state: "",
    runClubName: ""
  });
  
  const [fieldTouched, setFieldTouched] = useState({
    name: false,
    email: false,
    phone: false,
    password: false,
    confirmPassword: false,
    userType: false,
    city: false,
    state: false,
    runClubName: false
  });
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Pre-fill user type from URL parameter (signup happens in the app only).
  useEffect(() => {
    const userType = searchParams.get('type');
    if (userType === 'runclub') {
      setRegisterData(prev => ({ ...prev, userType: 'runclub' }));
    }
  }, [searchParams]);

  // Validation functions
  const validateLoginEmailOrPhone = (emailOrPhone) => {
    if (!emailOrPhone.trim()) return "Email or phone number is required";
    // Check if it's an email (contains @) or phone number
    if (emailOrPhone.includes('@')) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailOrPhone)) return "Please enter a valid email address";
    } else {
      const cleaned = emailOrPhone.replace(/[^\d+]/g, '');
      if (cleaned.length === 0) return "Please enter a valid phone number";
      if (!cleaned.startsWith('+') || cleaned.length < 8) {
        return "Enter a valid phone number with country code (e.g. +1 for US)";
      }
    }
    return "";
  };

  const validateLoginPassword = (password) => {
    if (!password) return "Password is required";
    return "";
  };

  const validateLoginUserType = (userType) => {
    if (!userType) return "Please select your account type";
    if (!["runner", "runclub"].includes(userType)) return "Please select a valid account type";
    return "";
  };

  const validateName = (name) => {
    if (!name.trim()) return "Full name is required";
    if (name.trim().length < 2) return "Name must be at least 2 characters";
    if (!/^[a-zA-Z\s'-]+$/.test(name.trim())) return "Name can only contain letters, spaces, hyphens, and apostrophes";
    return "";
  };

  const validateEmail = (email) => {
    if (!email.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email address";
    return "";
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters long";
    if (password.length > 128) return "Password must be less than 128 characters";
    if (!/[A-Z]/.test(password)) return "Password must contain at least one uppercase letter";
    if (!/[a-z]/.test(password)) return "Password must contain at least one lowercase letter";
    if (!/\d/.test(password)) return "Password must contain at least one digit";
    return "";
  };

  const validateConfirmPassword = (password, confirmPassword) => {
    if (!confirmPassword) return "Please confirm your password";
    if (password !== confirmPassword) return "Passwords don't match";
    return "";
  };

  const validateLocation = (location) => {
    if (!location.trim()) return "City is required";
    if (location.trim().length < 2) return "City must be at least 2 characters";
    return "";
  };

  const validateState = (state) => {
    // State is optional, so no validation needed
    return "";
  };

  const formatPhoneNumber = (value) => sanitizePhoneInput(value);

  // Use centralized validation functions
  const validatePhone = (phone) => {
    if (!phone) return ""; // Phone is optional
    return validatePhoneUtil(phone) || "";
  };

  const validateUserType = (userType) => {
    return validateUserTypeUtil(userType) || "";
  };

  const validateRunClubName = (runClubName) => {
    if (!runClubName) return "Run club name is required";
    if (runClubName.length < 2) return "Run club name must be at least 2 characters";
    if (runClubName.length > 50) return "Run club name must be less than 50 characters";
    return "";
  };

  // Real-time validation
  const validateField = (fieldName, value) => {
    let error = "";
    switch (fieldName) {
      case "name":
        error = validateName(value);
        break;
      case "email":
        error = validateEmail(value);
        break;
      case "phone":
        error = validatePhone(value);
        break;
      case "password":
        error = validatePassword(value);
        break;
      case "confirmPassword":
        error = validateConfirmPassword(registerData.password, value);
        break;
      case "userType":
        error = validateUserType(value);
        break;
      case "city":
        error = validateLocation(value);
        break;
      case "state":
        error = validateState(value);
        break;
      case "runClubName":
        error = validateRunClubName(value);
        break;
      default:
        break;
    }
    return error;
  };

  // Handle login field changes with validation
  const handleLoginFieldChange = (fieldName, value) => {
    setLoginData(prev => ({ ...prev, [fieldName]: value }));
    
    // Clear errors when user starts typing
    if (loginErrors[fieldName]) {
      setLoginErrors(prev => ({ ...prev, [fieldName]: "" }));
    }
    if (loginErrors.general) {
      setLoginErrors(prev => ({ ...prev, general: "" }));
    }
  };

  // Handle login field blur with validation
  const handleLoginFieldBlur = (fieldName) => {
    setLoginFieldTouched(prev => ({ ...prev, [fieldName]: true }));
    let error = "";
    switch (fieldName) {
      case "email":
        error = validateLoginEmailOrPhone(loginData.emailOrPhone);
        break;
      case "password":
        error = validateLoginPassword(loginData.password);
        break;
      case "userType":
        error = validateLoginUserType(loginData.userType);
        break;
      default:
        break;
    }
    // Ensure error is always a string
    const errorString = typeof error === 'string' ? error : '';
    setLoginErrors(prev => ({ ...prev, [fieldName]: errorString }));
  };

  // Handle field changes with validation
  const handleRegisterFieldChange = (fieldName, value) => {
    // Format phone number if it's the phone field
    const formattedValue = fieldName === 'phone' ? formatPhoneNumber(value) : value;
    
    setRegisterData(prev => ({ ...prev, [fieldName]: formattedValue }));
    
    // Clear error when user starts typing
    if (registerErrors[fieldName]) {
      setRegisterErrors(prev => ({ ...prev, [fieldName]: "" }));
    }
  };

  // Handle field blur with validation
  const handleRegisterFieldBlur = (fieldName) => {
    setFieldTouched(prev => ({ ...prev, [fieldName]: true }));
    const error = validateField(fieldName, registerData[fieldName]);
    // Ensure error is always a string
    const errorString = typeof error === 'string' ? error : '';
    setRegisterErrors(prev => ({ ...prev, [fieldName]: errorString }));
  };

  // Check if form is valid
  const isFormValid = () => {
    const nameError = validateName(registerData.name);
    const emailError = validateEmail(registerData.email);
    const phoneError = validatePhone(registerData.phone);
    const passwordError = validatePassword(registerData.password);
    const confirmPasswordError = validateConfirmPassword(registerData.password, registerData.confirmPassword);
    const userTypeError = validateUserType(registerData.userType);
    const cityError = validateLocation(registerData.city);
    const stateError = validateState(registerData.state);
    const runClubNameError = registerData.userType === "runclub" ? validateRunClubName(registerData.runClubName) : "";
    
    return !nameError && !emailError && !phoneError && !passwordError && !confirmPasswordError && !userTypeError && !cityError && !stateError && !runClubNameError;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched to show validation errors
    setLoginFieldTouched({
      emailOrPhone: true,
      password: true,
      userType: true
    });
    
    // Validate all fields
    const emailOrPhoneError = validateLoginEmailOrPhone(loginData.emailOrPhone);
    const passwordError = validateLoginPassword(loginData.password);
    const userTypeError = validateLoginUserType(loginData.userType);
    
    setLoginErrors({
      emailOrPhone: emailOrPhoneError,
      password: passwordError,
      userType: userTypeError,
      general: ""
    });
    
    // If there are validation errors, don't submit
    if (emailOrPhoneError || passwordError || userTypeError) {
      toast.error("Please fix the errors below");
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await login(loginData.emailOrPhone, loginData.password, loginData.userType);
      
      if (result.success) {
        toast.success("Login successful!");
        const isMaster =
        Boolean(MASTER_EMAIL && result.user?.email?.trim?.()) &&
        result.user.email.trim().toLowerCase() === MASTER_EMAIL.trim().toLowerCase();
        // Defer navigation so auth state is committed before route change (avoids guards seeing stale state)
        const target = isMaster ? '/console' : '/';
        setTimeout(() => navigate(target), 0);
      } else {
        
        // Handle specific login errors with improved messaging
        let errorMessage = result.error;
        let fieldErrors = { emailOrPhone: "", password: "", general: "" };
        
        if (result.error.includes("No account found with this email address") || result.error.includes("No account found with this phone number")) {
          errorMessage = result.error;
          fieldErrors.emailOrPhone = result.error.includes("email") ? "No account found with this email" : "No account found with this phone number";
        } else if (result.error.includes("Incorrect password")) {
          errorMessage = result.error;
          fieldErrors.password = "Incorrect password";
        } else if (result.error.includes("No") && (result.error.includes("account found with this email") || result.error.includes("account found with this phone"))) {
          // Handle user type mismatch
          errorMessage = result.error;
          fieldErrors.emailOrPhone = "Wrong account type";
        } else if (result.error.includes("Invalid email or password") || result.error.includes("Invalid phone number or password")) {
          errorMessage = result.error.includes("email") ? "Invalid email or password. Please check your credentials and try again." : "Invalid phone number or password. Please check your credentials and try again.";
          fieldErrors.general = result.error.includes("email") ? "Invalid email or password" : "Invalid phone number or password";
        } else if (result.error.includes("Account locked") || result.error.includes("Too many attempts")) {
          errorMessage = "Account temporarily locked due to too many failed attempts. Please try again later.";
          fieldErrors.general = "Account temporarily locked";
        } else if (result.error.includes("Network") || result.error.includes("connection")) {
          errorMessage = "Unable to connect to server. Please check your internet connection and try again.";
          fieldErrors.general = "Connection error";
        } else if (result.error.includes("Server error") || result.error.includes("500")) {
          errorMessage = "Server error occurred. Please try again in a few moments.";
          fieldErrors.general = "Server error";
        } else {
          // Use the backend error message directly for better specificity
          errorMessage = result.error || "Login failed. Please try again.";
          fieldErrors.general = errorMessage;
        }
        
        setLoginErrors(fieldErrors);
        toast.error(errorMessage);
      }
    } catch (error) {
      
      let errorMessage = "Login failed. Please try again.";
      // Key must be emailOrPhone (not email) — that's what setLoginErrors and the
      // input bindings read; an `email` key here meant field errors never rendered.
      let fieldErrors = { emailOrPhone: "", password: "", general: "" };

      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const detail = error.response.data?.detail || '';
        
        switch (status) {
          case 401:
            errorMessage = "Invalid email or password. Please check your credentials.";
            fieldErrors.general = "Invalid credentials";
            break;
          case 403:
            errorMessage = "Account access denied. Please contact support if this continues.";
            fieldErrors.general = "Access denied";
            break;
          case 404:
            errorMessage = "Account not found. Please check your email or phone number.";
            fieldErrors.emailOrPhone = "Account not found";
            break;
          case 429:
            errorMessage = "Too many login attempts. Please wait a few minutes before trying again.";
            fieldErrors.general = "Too many attempts";
            break;
          case 500:
            errorMessage = "Server error. Please try again in a few moments.";
            fieldErrors.general = "Server error";
            break;
          default:
            errorMessage = detail || "Login failed. Please try again.";
            fieldErrors.general = errorMessage;
        }
      } else if (error.request) {
        // Network error
        errorMessage = "Unable to connect to server. Please check your internet connection and try again.";
        fieldErrors.general = "Connection error";
      } else {
        // Other error
        errorMessage = "An unexpected error occurred. Please try again.";
        fieldErrors.general = "Unexpected error";
      }
      
      setLoginErrors(fieldErrors);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched to show validation errors
    setFieldTouched({
      name: true,
      email: true,
      phone: true,
      password: true,
      confirmPassword: true,
      userType: true,
      location: true,
      state: true,
      runClubName: true
    });
    
    // Validate all fields
    const nameError = validateName(registerData.name);
    const emailError = validateEmail(registerData.email);
    const phoneError = validatePhone(registerData.phone);
    const passwordError = validatePassword(registerData.password);
    const confirmPasswordError = validateConfirmPassword(registerData.password, registerData.confirmPassword);
    const userTypeError = validateUserType(registerData.userType);
    const cityError = validateLocation(registerData.city);
    const stateError = validateState(registerData.state);
    const runClubNameError = registerData.userType === "runclub" ? validateRunClubName(registerData.runClubName) : "";
    
    setRegisterErrors({
      name: nameError,
      email: emailError,
      phone: phoneError,
      password: passwordError,
      confirmPassword: confirmPasswordError,
      userType: userTypeError,
      city: cityError,
      state: stateError,
      runClubName: runClubNameError
    });
    
    // If there are validation errors, don't submit
    if (nameError || emailError || phoneError || passwordError || confirmPasswordError || userTypeError || cityError || stateError || runClubNameError) {
      toast.error("Please fix the errors below");
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await register(registerData.email, registerData.password, registerData.name, registerData.phone, registerData.userType, registerData.city, registerData.state, registerData.runClubName);
      if (result.success) {
        toast.success("Registration successful! Welcome to Runcards!");
        navigate('/');
      } else {
        // Handle specific backend errors
        let errorMessage = result.error;
        
        // Debug: Check if result.error is an object
        if (typeof result.error === 'object' && result.error !== null) {
          console.error('result.error is an object:', result.error);
          errorMessage = 'Please check your input and try again';
        } else if (typeof result.error === 'string' && result.error.includes("Email already registered")) {
          errorMessage = "This email is already registered. Please try logging in instead.";
          setRegisterErrors(prev => ({ ...prev, email: "This email is already registered" }));
        } else if (typeof result.error === 'string' && result.error.includes("Invalid email format")) {
          errorMessage = "Please enter a valid email address";
          setRegisterErrors(prev => ({ ...prev, email: "Invalid email format" }));
        } else if (typeof result.error === 'string' && (result.error.includes("Password") || result.error.includes("at least 8 characters") || result.error.includes("uppercase") || result.error.includes("lowercase") || result.error.includes("digit"))) {
          errorMessage = "Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit";
          setRegisterErrors(prev => ({ ...prev, password: "Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit" }));
        }
        
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error("Registration failed. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rc-auth relative min-h-screen bg-black text-white">
      <style>{`
        /* Page-scoped tokens so all the form's existing runcards-* utility
           classes pick up the dark theme inside this page only. */
        .rc-auth {
          --runcards-background: #14151a;
          --runcards-foreground: rgba(255,255,255,0.04);
          --runcards-border: rgba(255,255,255,0.14);
          --runcards-text-primary: #f5f6f8;
          --runcards-text-secondary: rgba(255,255,255,0.78);
          --runcards-text-tertiary: rgba(255,255,255,0.5);
          --runcards-accent: #ffffff;
          --runcards-accent-hover: #e6e7ea;
          --runcards-surface: rgba(255,255,255,0.06);
        }
        .rc-auth input,
        .rc-auth [role="combobox"] {
          background-color: rgba(255,255,255,0.05) !important;
          border-color: rgba(255,255,255,0.14) !important;
          color: #f5f6f8 !important;
        }
        .rc-auth input::placeholder {
          color: rgba(255,255,255,0.4) !important;
        }
        .rc-auth button:focus,
        .rc-auth a:focus,
        .rc-auth input:focus {
          outline: none !important;
          box-shadow: none !important;
        }
        .rc-auth button:focus-visible,
        .rc-auth a:focus-visible,
        .rc-auth input:focus-visible {
          outline: none !important;
          box-shadow: 0 0 0 2px rgba(255,255,255,0.18) !important;
        }
      `}</style>

      {/* Ambient gradient — matches the marketing dark sections. */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          background:
            "radial-gradient(circle at 30% 20%, rgba(120,127,154,0.18), transparent 55%), radial-gradient(circle at 75% 80%, rgba(58,106,76,0.14), transparent 55%)",
        }}
      />

      <div className="relative mx-auto flex min-h-screen max-w-md flex-col px-5 py-8 sm:px-6 sm:py-10">
        {/* Back link */}
        <Link
          to="/"
          className="mb-10 inline-flex w-fit items-center font-runcards text-sm text-white/55 transition-colors hover:text-white"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to home
        </Link>

        {/* Brand mark + headline */}
        <div className="mb-8 text-center">
          {logoError ? (
            <span className="font-runcards-bold text-2xl tracking-tight text-white">RunCards</span>
          ) : (
            <img
              src="/RunCards_full_white_text_transparent.png"
              alt="RunCards"
              className="mx-auto mb-6 h-12 w-auto object-contain sm:h-14"
              onError={() => setLogoError(true)}
            />
          )}
          <p className="mb-3 font-runcards-bold text-[11px] uppercase tracking-[0.32em] text-white/45">
            Welcome back
          </p>
          <h1 className="font-runcards-black text-[clamp(1.75rem,4vw,2.5rem)] leading-[1.05] tracking-[-0.02em]">
            Log in to RunCards.
          </h1>
        </div>

        {/* Form panel — login-only on the web; signup happens in the mobile app. */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md sm:p-6">
              
                  {/* Login Form */}
                  {activeTab === 'login' && (
                    <form onSubmit={handleLogin} className="space-y-3">
                      {/* General error message */}
                      {loginErrors.general && (
                        <div className="bg-runcards-error/20 border border-runcards-error rounded-lg p-3">
                          <div className="flex items-center text-runcards-error text-sm font-runcards">
                            <AlertCircle className="w-4 h-4 mr-2" />
                            {typeof loginErrors.general === 'string' ? loginErrors.general : 'An error occurred'}
                          </div>
                        </div>
                      )}
                      
                      {/* User Type */}
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleLoginFieldChange('userType', 'runner')}
                          className={`px-3 py-2 font-runcards text-sm transition-colors ${
                            (loginData.userType || 'runner') === 'runner'
                              ? 'text-runcards-accent font-runcards-bold underline underline-offset-2'
                              : 'text-runcards-text-tertiary hover:text-runcards-text-primary'
                          }`}
                        >
                          Runner
                        </button>
                        <span className="text-runcards-text-tertiary text-sm">|</span>
                        <button
                          type="button"
                          onClick={() => handleLoginFieldChange('userType', 'runclub')}
                          className={`px-3 py-2 font-runcards text-sm transition-colors ${
                            loginData.userType === 'runclub'
                              ? 'text-runcards-accent font-runcards-bold underline underline-offset-2'
                              : 'text-runcards-text-tertiary hover:text-runcards-text-primary'
                          }`}
                        >
                          Run Club
                        </button>
                      </div>
                      
                      {/* Email or Phone */}
                      <div>
                        <Label htmlFor="login-emailOrPhone" className="text-runcards-text-primary font-runcards-bold text-sm" >Email or Phone Number</Label>
                        <Input
                          id="login-emailOrPhone"
                          type="text"
                          placeholder="john62martinez@gmail.com or +1 555 123 4567"
                          value={loginData.emailOrPhone}
                          onChange={(e) => handleLoginFieldChange('emailOrPhone', e.target.value)}
                          onBlur={() => handleLoginFieldBlur('emailOrPhone')}
                          className={`mt-1 h-11 bg-runcards-background border-runcards-border text-runcards-text-primary placeholder:text-runcards-text-tertiary ${
                            loginFieldTouched.emailOrPhone && loginErrors.emailOrPhone
                              ? 'border-runcards-error focus:border-runcards-error focus:ring-runcards-error/30'
                              : 'border-runcards-border focus:border-runcards-accent focus:ring-runcards-accent/30'
                          }`}
                          required
                        />
                        {loginFieldTouched.emailOrPhone && loginErrors.emailOrPhone && (
                          <div className="flex items-center mt-1 text-runcards-error text-xs font-runcards">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {typeof loginErrors.emailOrPhone === 'string' ? loginErrors.emailOrPhone : 'Invalid input'}
                          </div>
                        )}
                      </div>
                      
                      {/* Password */}
                      <div>
                        <Label htmlFor="login-password" className="text-runcards-text-primary font-runcards-bold text-sm" >Password</Label>
                        <div className="relative mt-1">
                          <Input
                            id="login-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Password"
                            value={loginData.password}
                            onChange={(e) => handleLoginFieldChange('password', e.target.value)}
                            onBlur={() => handleLoginFieldBlur('password')}
className={`pr-10 h-11 bg-runcards-background border-runcards-border text-runcards-text-primary placeholder:text-runcards-text-tertiary items-center ${
                            loginFieldTouched.password && loginErrors.password
                                ? 'border-runcards-error focus:border-runcards-error focus:ring-runcards-error/30'
                                : 'border-runcards-border focus:border-runcards-accent focus:ring-runcards-accent/30'
                            }`}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-runcards-text-tertiary hover:text-runcards-text-secondary"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {loginFieldTouched.password && loginErrors.password && (
                          <div className="flex items-center mt-1 text-runcards-error text-xs font-runcards">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {typeof loginErrors.password === 'string' ? loginErrors.password : 'Invalid input'}
                          </div>
                        )}
                        <div className="flex justify-end mt-1">
                          <Link
                            to="/forgot-password"
                            className="text-runcards-accent hover:text-runcards-text-primary text-xs font-runcards-bold"
                          >
                            Forgot Password?
                          </Link>
                        </div>
                      </div>
                      
                      {/* Login Button */}
                      <Button
                        type="submit"
                        className="h-11 w-full rounded-xl bg-white font-runcards-bold text-sm text-black transition-all hover:bg-white/90 disabled:opacity-50"
                        disabled={loading || (loginFieldTouched.emailOrPhone && loginErrors.emailOrPhone) || (loginFieldTouched.password && loginErrors.password)}
                      >
                        {loading ? "Logging in…" : "Log in"}
                      </Button>

                      {/* Early access: the website is admin-only for now.
                          Runners and clubs sign in through the RunCards app. */}
                      <p className="pt-2 text-center font-runcards text-xs leading-relaxed text-white/45">
                        RunCards is in early access — the website is for admins.
                        <br />
                        Runners &amp; clubs: log in through the RunCards app.
                      </p>
                    </form>
                  )}
              
                  {/* Registration Form */}
                  {activeTab === 'register' && (
                    <form onSubmit={handleRegister} className="space-y-3">
                      {/* Full Name */}
                      <div>
                        <Label htmlFor="register-name" className="text-runcards-text-primary font-runcards-bold text-sm" >Full Name</Label>
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="Enter your full name"
                          value={registerData.name}
                          onChange={(e) => handleRegisterFieldChange('name', e.target.value)}
                          onBlur={() => handleRegisterFieldBlur('name')}
                          className={`mt-1 h-11 bg-runcards-background border-runcards-border text-runcards-text-primary placeholder:text-runcards-text-tertiary ${
                            fieldTouched.name && registerErrors.name
                              ? 'border-runcards-error focus:border-runcards-error focus:ring-runcards-error/30'
                              : 'border-runcards-border focus:border-runcards-accent focus:ring-runcards-accent/30'
                          }`}
                          required
                        />
                        {fieldTouched.name && registerErrors.name && (
                          <div className="flex items-center mt-1 text-runcards-error text-xs font-runcards">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {typeof registerErrors.name === 'string' ? registerErrors.name : 'Invalid input'}
                          </div>
                        )}
                      </div>
                  
                      {/* User Type */}
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleRegisterFieldChange('userType', 'runner')}
                          className={`font-runcards text-sm transition-colors ${
                            (registerData.userType || 'runner') === 'runner'
                              ? 'text-runcards-accent font-runcards-bold underline underline-offset-2'
                              : 'text-runcards-text-tertiary hover:text-runcards-text-primary'
                          }`}
                        >
                          Runner
                        </button>
                        <span className="text-runcards-text-tertiary text-sm">|</span>
                        <button
                          type="button"
                          onClick={() => handleRegisterFieldChange('userType', 'runclub')}
                          className={`font-runcards text-sm transition-colors ${
                            registerData.userType === 'runclub'
                              ? 'text-runcards-accent font-runcards-bold underline underline-offset-2'
                              : 'text-runcards-text-tertiary hover:text-runcards-text-primary'
                          }`}
                        >
                          Run Club
                        </button>
                      </div>
                      
                      {/* Location and State fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="register-city" className="text-runcards-text-primary font-runcards-bold text-sm" >City</Label>
                          <Input
                            id="register-city"
                            placeholder="Enter your city"
                            value={registerData.city}
                            onChange={(e) => handleRegisterFieldChange('city', e.target.value)}
                            onBlur={() => handleRegisterFieldBlur('city')}
                            className={`mt-1 h-11 bg-runcards-background border-runcards-border text-runcards-text-primary placeholder:text-runcards-text-tertiary ${
                              fieldTouched.city && registerErrors.city
                                ? 'border-runcards-error focus:border-runcards-error focus:ring-runcards-error/30'
                                : 'border-runcards-border focus:border-runcards-accent focus:ring-runcards-accent/30'
                            }`}
                          />
                          {fieldTouched.city && registerErrors.city && (
                            <div className="flex items-center mt-1 text-runcards-error text-xs font-runcards">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              {registerErrors.city}
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <Label htmlFor="register-state" className="text-runcards-text-primary font-runcards-bold text-sm" >State</Label>
                          <Select
                            value={registerData.state}
                            onValueChange={(value) => handleRegisterFieldChange('state', value)}
                          >
                            <SelectTrigger 
                              className={`mt-1 bg-runcards-background border-runcards-border text-runcards-text-primary focus:border-runcards-accent focus:ring-runcards-accent/30 h-11 ${
                                fieldTouched.state && registerErrors.state
                                  ? 'border-runcards-error focus:border-runcards-error focus:ring-runcards-error/30'
                                  : 'border-runcards-border focus:border-runcards-accent focus:ring-runcards-accent/30'
                              }`}
                            >
                              <SelectValue placeholder="Select state" />
                            </SelectTrigger>
                            <SelectContent className="bg-runcards-foreground border-runcards-border max-h-60">
                              <SelectItem value="AL" className="text-runcards-text-primary hover:bg-runcards-surface">AL - Alabama</SelectItem>
                              <SelectItem value="AK" className="text-runcards-text-primary hover:bg-runcards-surface">AK - Alaska</SelectItem>
                              <SelectItem value="AZ" className="text-runcards-text-primary hover:bg-runcards-surface">AZ - Arizona</SelectItem>
                              <SelectItem value="AR" className="text-runcards-text-primary hover:bg-runcards-surface">AR - Arkansas</SelectItem>
                              <SelectItem value="CA" className="text-runcards-text-primary hover:bg-runcards-surface">CA - California</SelectItem>
                              <SelectItem value="CO" className="text-runcards-text-primary hover:bg-runcards-surface">CO - Colorado</SelectItem>
                              <SelectItem value="CT" className="text-runcards-text-primary hover:bg-runcards-surface">CT - Connecticut</SelectItem>
                              <SelectItem value="DE" className="text-runcards-text-primary hover:bg-runcards-surface">DE - Delaware</SelectItem>
                              <SelectItem value="FL" className="text-runcards-text-primary hover:bg-runcards-surface">FL - Florida</SelectItem>
                              <SelectItem value="GA" className="text-runcards-text-primary hover:bg-runcards-surface">GA - Georgia</SelectItem>
                              <SelectItem value="HI" className="text-runcards-text-primary hover:bg-runcards-surface">HI - Hawaii</SelectItem>
                              <SelectItem value="ID" className="text-runcards-text-primary hover:bg-runcards-surface">ID - Idaho</SelectItem>
                              <SelectItem value="IL" className="text-runcards-text-primary hover:bg-runcards-surface">IL - Illinois</SelectItem>
                              <SelectItem value="IN" className="text-runcards-text-primary hover:bg-runcards-surface">IN - Indiana</SelectItem>
                              <SelectItem value="IA" className="text-runcards-text-primary hover:bg-runcards-surface">IA - Iowa</SelectItem>
                              <SelectItem value="KS" className="text-runcards-text-primary hover:bg-runcards-surface">KS - Kansas</SelectItem>
                              <SelectItem value="KY" className="text-runcards-text-primary hover:bg-runcards-surface">KY - Kentucky</SelectItem>
                              <SelectItem value="LA" className="text-runcards-text-primary hover:bg-runcards-surface">LA - Louisiana</SelectItem>
                              <SelectItem value="ME" className="text-runcards-text-primary hover:bg-runcards-surface">ME - Maine</SelectItem>
                              <SelectItem value="MD" className="text-runcards-text-primary hover:bg-runcards-surface">MD - Maryland</SelectItem>
                              <SelectItem value="MA" className="text-runcards-text-primary hover:bg-runcards-surface">MA - Massachusetts</SelectItem>
                              <SelectItem value="MI" className="text-runcards-text-primary hover:bg-runcards-surface">MI - Michigan</SelectItem>
                              <SelectItem value="MN" className="text-runcards-text-primary hover:bg-runcards-surface">MN - Minnesota</SelectItem>
                              <SelectItem value="MS" className="text-runcards-text-primary hover:bg-runcards-surface">MS - Mississippi</SelectItem>
                              <SelectItem value="MO" className="text-runcards-text-primary hover:bg-runcards-surface">MO - Missouri</SelectItem>
                              <SelectItem value="MT" className="text-runcards-text-primary hover:bg-runcards-surface">MT - Montana</SelectItem>
                              <SelectItem value="NE" className="text-runcards-text-primary hover:bg-runcards-surface">NE - Nebraska</SelectItem>
                              <SelectItem value="NV" className="text-runcards-text-primary hover:bg-runcards-surface">NV - Nevada</SelectItem>
                              <SelectItem value="NH" className="text-runcards-text-primary hover:bg-runcards-surface">NH - New Hampshire</SelectItem>
                              <SelectItem value="NJ" className="text-runcards-text-primary hover:bg-runcards-surface">NJ - New Jersey</SelectItem>
                              <SelectItem value="NM" className="text-runcards-text-primary hover:bg-runcards-surface">NM - New Mexico</SelectItem>
                              <SelectItem value="NY" className="text-runcards-text-primary hover:bg-runcards-surface">NY - New York</SelectItem>
                              <SelectItem value="NC" className="text-runcards-text-primary hover:bg-runcards-surface">NC - North Carolina</SelectItem>
                              <SelectItem value="ND" className="text-runcards-text-primary hover:bg-runcards-surface">ND - North Dakota</SelectItem>
                              <SelectItem value="OH" className="text-runcards-text-primary hover:bg-runcards-surface">OH - Ohio</SelectItem>
                              <SelectItem value="OK" className="text-runcards-text-primary hover:bg-runcards-surface">OK - Oklahoma</SelectItem>
                              <SelectItem value="OR" className="text-runcards-text-primary hover:bg-runcards-surface">OR - Oregon</SelectItem>
                              <SelectItem value="PA" className="text-runcards-text-primary hover:bg-runcards-surface">PA - Pennsylvania</SelectItem>
                              <SelectItem value="RI" className="text-runcards-text-primary hover:bg-runcards-surface">RI - Rhode Island</SelectItem>
                              <SelectItem value="SC" className="text-runcards-text-primary hover:bg-runcards-surface">SC - South Carolina</SelectItem>
                              <SelectItem value="SD" className="text-runcards-text-primary hover:bg-runcards-surface">SD - South Dakota</SelectItem>
                              <SelectItem value="TN" className="text-runcards-text-primary hover:bg-runcards-surface">TN - Tennessee</SelectItem>
                              <SelectItem value="TX" className="text-runcards-text-primary hover:bg-runcards-surface">TX - Texas</SelectItem>
                              <SelectItem value="UT" className="text-runcards-text-primary hover:bg-runcards-surface">UT - Utah</SelectItem>
                              <SelectItem value="VT" className="text-runcards-text-primary hover:bg-runcards-surface">VT - Vermont</SelectItem>
                              <SelectItem value="VA" className="text-runcards-text-primary hover:bg-runcards-surface">VA - Virginia</SelectItem>
                              <SelectItem value="WA" className="text-runcards-text-primary hover:bg-runcards-surface">WA - Washington</SelectItem>
                              <SelectItem value="WV" className="text-runcards-text-primary hover:bg-runcards-surface">WV - West Virginia</SelectItem>
                              <SelectItem value="WI" className="text-runcards-text-primary hover:bg-runcards-surface">WI - Wisconsin</SelectItem>
                              <SelectItem value="WY" className="text-runcards-text-primary hover:bg-runcards-surface">WY - Wyoming</SelectItem>
                            </SelectContent>
                          </Select>
                          {fieldTouched.state && registerErrors.state && (
                            <div className="flex items-center mt-1 text-runcards-error text-xs font-runcards">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              {registerErrors.state}
                            </div>
                          )}
                        </div>
                      </div>
                  
                      {/* Conditional Run Club Name field */}
                      {registerData.userType === "runclub" && (
                        <div>
                          <Label htmlFor="register-run-club-name" className="text-runcards-text-primary font-runcards-bold text-sm" >Run Club Name</Label>
                          <Input
                            id="register-run-club-name"
                            type="text"
                            placeholder="Enter your run club name"
                            value={registerData.runClubName}
                            onChange={(e) => handleRegisterFieldChange('runClubName', e.target.value)}
                            onBlur={() => handleRegisterFieldBlur('runClubName')}
                            className={`mt-1 h-11 bg-runcards-background border-runcards-border text-runcards-text-primary placeholder:text-runcards-text-tertiary ${
                              fieldTouched.runClubName && registerErrors.runClubName
                                ? 'border-runcards-error focus:border-runcards-error focus:ring-runcards-error/30'
                                : 'border-runcards-border focus:border-runcards-accent focus:ring-runcards-accent/30'
                            }`}
                          />
                          {fieldTouched.runClubName && registerErrors.runClubName && (
                            <div className="flex items-center mt-1 text-runcards-error text-xs font-runcards">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              {registerErrors.runClubName}
                            </div>
                          )}
                        </div>
                      )}
                  
                      {/* Email */}
                      <div>
                        <Label htmlFor="register-email" className="text-runcards-text-primary font-runcards-bold text-sm" >Email Address</Label>
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="Enter your email"
                          value={registerData.email}
                          onChange={(e) => handleRegisterFieldChange('email', e.target.value)}
                          onBlur={() => handleRegisterFieldBlur('email')}
                          className={`mt-1 h-11 bg-runcards-background border-runcards-border text-runcards-text-primary placeholder:text-runcards-text-tertiary ${
                            fieldTouched.email && registerErrors.email
                              ? 'border-runcards-error focus:border-runcards-error focus:ring-runcards-error/30'
                              : 'border-runcards-border focus:border-runcards-accent focus:ring-runcards-accent/30'
                          }`}
                          required
                        />
                        {fieldTouched.email && registerErrors.email && (
                          <div className="flex items-center mt-1 text-runcards-error text-xs font-runcards">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {registerErrors.email}
                          </div>
                        )}
                      </div>
                      
                      {/* Phone */}
                      <div>
                        <Label htmlFor="register-phone" className="text-runcards-text-primary font-runcards-bold text-sm" >Phone Number</Label>
                        <Input
                          id="register-phone"
                          type="tel"
                          placeholder="+1 555 123 4567"
                          value={registerData.phone}
                          onChange={(e) => handleRegisterFieldChange('phone', e.target.value)}
                          onBlur={() => handleRegisterFieldBlur('phone')}
                          className={`mt-1 h-11 bg-runcards-background border-runcards-border text-runcards-text-primary placeholder:text-runcards-text-tertiary ${
                            fieldTouched.phone && registerErrors.phone
                              ? 'border-runcards-error focus:border-runcards-error focus:ring-runcards-error/30'
                              : 'border-runcards-border focus:border-runcards-accent focus:ring-runcards-accent/30'
                          }`}
                          maxLength={16}
                          required
                        />
                        {fieldTouched.phone && registerErrors.phone && (
                          <div className="flex items-center mt-1 text-runcards-error text-xs font-runcards">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {registerErrors.phone}
                          </div>
                        )}
                      </div>
                  
                      {/* Password */}
                      <div>
                        <Label htmlFor="register-password" className="text-runcards-text-primary font-runcards-bold text-sm" >Password</Label>
                        <div className="relative mt-1">
                          <Input
                            id="register-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Create a password"
                            value={registerData.password}
                            onChange={(e) => handleRegisterFieldChange('password', e.target.value)}
                            onBlur={() => handleRegisterFieldBlur('password')}
                            className={`pr-10 h-11 bg-runcards-background border-runcards-border text-runcards-text-primary placeholder:text-runcards-text-tertiary ${
                              fieldTouched.password && registerErrors.password
                                ? 'border-runcards-error focus:border-runcards-error focus:ring-runcards-error/30'
                                : 'border-runcards-border focus:border-runcards-accent focus:ring-runcards-accent/30'
                            }`}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-runcards-text-tertiary hover:text-runcards-text-secondary"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {fieldTouched.password && registerErrors.password && (
                          <div className="flex items-center mt-1 text-runcards-error text-xs font-runcards">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {typeof registerErrors.password === 'string' ? registerErrors.password : 'Invalid input'}
                          </div>
                        )}
                      </div>
                      
                      {/* Confirm Password */}
                      <div>
                        <Label htmlFor="register-confirm-password" className="text-runcards-text-primary font-runcards-bold text-sm" >Confirm Password</Label>
                        <div className="relative mt-1">
                          <Input
                            id="register-confirm-password"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm your password"
                            value={registerData.confirmPassword}
                            onChange={(e) => handleRegisterFieldChange('confirmPassword', e.target.value)}
                            onBlur={() => handleRegisterFieldBlur('confirmPassword')}
                            className={`pr-10 h-11 bg-runcards-background border-runcards-border text-runcards-text-primary placeholder:text-runcards-text-tertiary ${
                              fieldTouched.confirmPassword && registerErrors.confirmPassword
                                ? 'border-runcards-error focus:border-runcards-error focus:ring-runcards-error/30'
                                : 'border-runcards-border focus:border-runcards-accent focus:ring-runcards-accent/30'
                            }`}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-runcards-text-tertiary hover:text-runcards-text-secondary"
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {fieldTouched.confirmPassword && registerErrors.confirmPassword && (
                          <div className="flex items-center mt-1 text-runcards-error text-xs font-runcards">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {registerErrors.confirmPassword}
                          </div>
                        )}
                      </div>
                      
                      {/* Create Account Button */}
                      <Button
                        type="submit"
                        className="h-11 w-full rounded-xl bg-white font-runcards-bold text-sm text-black transition-all hover:bg-white/90 disabled:opacity-50"
                        disabled={loading || !isFormValid()}
                      >
                        {loading ? "Creating account…" : "Create account"}
                      </Button>

                      {/* Divider + Google Sign-In */}
                      <div className="relative my-2">
                        <div className="absolute inset-0 flex items-center" aria-hidden>
                          <div className="w-full border-t border-white/10" />
                        </div>
                        <div className="relative flex justify-center">
                          <span className="bg-[#14151a] px-3 font-runcards text-[10px] uppercase tracking-[0.24em] text-white/40">
                            Or continue with
                          </span>
                        </div>
                      </div>
                      <GoogleSignInButton
                        userType={registerData.userType || 'runner'}
                        className="w-full"
                      />
                    </form>
                  )}
        </div>

        {/* Signup is mobile-app only. */}
        <p className="mt-6 text-center font-runcards text-sm text-white/55">
          Don't have an account?{" "}
          <span className="font-runcards-bold text-white">
            Sign up in the RunCards app.
          </span>
        </p>
      </div>
    </div>
  );
};

export default AuthPage;